import { CODIGOS_DE_RECUSA, esperaMs, lerRecusa } from '@viewer/room/roomClient'
import { lerAviso, type AvisoDoServidor } from './clashPartilha'
import { urlDoCanal } from './clashPartilhaPorta'

// ---------------------------------------------------------------------------
// O canal ao vivo da triagem partilhada: o fio.
//
// O canal so avisa: "ha versao nova", quem esta, quem esta a editar o
// que. Os dados vem sempre pela API (ver backend/modules/clash/canal.py).
// Por isso uma mensagem perdida nao perde nada, e sem canal a partilha
// continua a funcionar (mais devagar: o motor passa a perguntar de 20 em
// 20 segundos).
//
// Reconexao com espera crescente, como a sala de revisao; uma recusa do
// servidor (1008, 1013) nao se repete. Um sinal de vida a cada 25 s: o
// servidor fecha as ligacoes caladas, para uma ligacao morta nao ficar a
// contar como presenca.
// ---------------------------------------------------------------------------

export interface Presente {
  id: number
  nome: string
  utilizador: string
  /** Identidade do conflito que esta a editar, ou null. */
  editar: string | null
  /** Ate quando (relogio deste browser, ms), se esta a editar. */
  editarAte: number | null
}

export interface EventosDoCanal {
  aoVersao: (versao: number, porId: string, por: string, avisos: AvisoDoServidor[]) => void
  aoPresenca: (presentes: Presente[], eu: number | null) => void
  aoEstado: (ligado: boolean) => void
  aoRecusar: (motivo: string) => void
}

export interface CanalDaPartilha {
  editar: (identidade: string | null) => void
  fechar: () => void
}

export type AbrirCanal = (projetoId: string, eventos: EventosDoCanal) => CanalDaPartilha

const SINAL_DE_VIDA_MS = 25_000

/** Le a presenca que o servidor manda. */
export function lerPresenca(v: unknown, agoraMs: number): Presente[] {
  if (!Array.isArray(v)) return []
  const out: Presente[] = []
  for (const x of v) {
    if (!x || typeof x !== 'object') continue
    const o = x as Record<string, unknown>
    if (typeof o.id !== 'number' || typeof o.nome !== 'string') continue
    const editar = typeof o.editar === 'string' && o.editar ? o.editar : null
    const restante = typeof o.restante_s === 'number' ? o.restante_s : null
    out.push({
      id: o.id,
      nome: o.nome.slice(0, 60),
      utilizador: typeof o.utilizador === 'string' ? o.utilizador : '',
      editar,
      editarAte: editar && restante !== null ? agoraMs + restante * 1000 : null,
    })
  }
  return out
}

export const abrirCanalWebSocket: AbrirCanal = (projetoId, eventos) => {
  let socket: WebSocket | null = null
  let fechado = false
  let tentativa = 0
  let reabrir: number | undefined
  let sinal: number | undefined
  let eu: number | null = null
  // O que se esta a editar, para voltar a dizer depois de uma reconexao.
  let aEditar: string | null = null

  const enviar = (m: unknown) => {
    if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(m))
  }

  const abrir = () => {
    if (fechado) return
    let s: WebSocket
    try {
      s = new WebSocket(urlDoCanal(projetoId))
    } catch {
      agendar()
      return
    }
    socket = s
    s.onopen = () => {
      tentativa = 0
      eventos.aoEstado(true)
      if (aEditar) enviar({ tipo: 'editar', chave: aEditar })
      window.clearInterval(sinal)
      sinal = window.setInterval(() => enviar({ tipo: 'ping' }), SINAL_DE_VIDA_MS)
    }
    s.onmessage = (ev: MessageEvent) => {
      let m: Record<string, unknown>
      try {
        const lido: unknown = JSON.parse(String(ev.data))
        if (!lido || typeof lido !== 'object') return
        m = lido as Record<string, unknown>
      } catch {
        return
      }
      const recusa = lerRecusa(m)
      if (recusa) {
        if (CODIGOS_DE_RECUSA.has(recusa.codigo)) {
          fechado = true
          socket = null
          window.clearInterval(sinal)
          eventos.aoEstado(false)
          eventos.aoRecusar(recusa.motivo || 'O servidor recusou o canal ao vivo.')
          s.close()
        }
        return
      }
      if (m.tipo === 'entrou' && typeof m.id === 'number') eu = m.id
      else if (m.tipo === 'presenca') eventos.aoPresenca(lerPresenca(m.participantes, Date.now()), eu)
      else if (m.tipo === 'versao' && typeof m.versao === 'number') {
        const avisos = Array.isArray(m.avisos)
          ? m.avisos.map(lerAviso).filter((a): a is AvisoDoServidor => a !== null)
          : []
        eventos.aoVersao(m.versao, String(m.por_id ?? ''), String(m.por ?? ''), avisos)
      }
    }
    s.onclose = (ev?: CloseEvent) => {
      socket = null
      window.clearInterval(sinal)
      eventos.aoEstado(false)
      if (fechado) return
      if (ev && CODIGOS_DE_RECUSA.has(ev.code)) {
        fechado = true
        eventos.aoRecusar(ev.reason || 'O servidor recusou o canal ao vivo.')
        return
      }
      agendar()
    }
    s.onerror = () => {
      // O onclose vem a seguir; e la que se trata da reconexao.
    }
  }

  const agendar = () => {
    const espera = esperaMs(tentativa)
    tentativa += 1
    reabrir = window.setTimeout(abrir, espera)
  }

  abrir()

  return {
    editar: (identidade) => {
      aEditar = identidade
      enviar({ tipo: 'editar', chave: identidade })
    },
    fechar: () => {
      fechado = true
      window.clearTimeout(reabrir)
      window.clearInterval(sinal)
      enviar({ tipo: 'saio' })
      socket?.close()
      socket = null
    },
  }
}
